export const teacherRoleAPI = {
  save: (teacherId, data) => fetch(`${API_BASE_URL}/teacher-role/${teacherId}`, {
    ...authHeaders(),
    method: 'POST',
    body: JSON.stringify(data)
  }).then(r => r.json().then(data => {
    if (!r.ok) throw new Error(data.error || 'Failed to save teacher role selections');
    return data;
  })),
  get: (teacherId) => fetch(`${API_BASE_URL}/teacher-role/${teacherId}`, authHeaders())
    .then(r => r.json().then(data => {
      if (!r.ok) throw new Error(data.error || 'Failed to fetch teacher role selections');
      return data;
    })),
  getAll: () => fetch(`${API_BASE_URL}/teacher-role`, authHeaders())
    .then(r => r.json().then(data => {
      if (!r.ok) throw new Error(data.error || 'Failed to fetch all teacher role selections');
      return data;
    })),
  delete: (teacherId) => fetch(`${API_BASE_URL}/teacher-role/${teacherId}`, {
    ...authHeaders(),
    method: 'DELETE'
  }).then(r => r.json().then(data => {
    if (!r.ok) throw new Error(data.error || 'Failed to delete teacher role');
    return data;
  })),
};

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const authHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    }
  };
};

// Returns headers with the incharge PIN from sessionStorage
const pinHeaders = (role) => {
  const pin = sessionStorage.getItem(role === 'canteen' ? 'vn_canteen_pin_session' : 'vn_akshay_pin_session') || '';
  return { 'Content-Type': 'application/json', 'x-incharge-pin': pin };
};


export const authAPI = {
  signup: (data) => fetch(`${API_BASE_URL}/auth/signup`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data)
  }).then(r => r.json().then(data => {
    if (!r.ok) throw new Error(data.error || 'Signup failed');
    return data;
  })),

  login: (data) => fetch(`${API_BASE_URL}/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data)
  }).then(r => r.json().then(data => {
    if (!r.ok) throw new Error(data.error || 'Login failed');
    return data;
  })),

  logout: () => localStorage.removeItem('token'),

  forgotPassword: (email) => fetch(`${API_BASE_URL}/auth/forgot-password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email })
  }).then(r => r.json().then(data => {
    if (!r.ok) throw new Error(data.error || 'Request failed');
    return data;
  })),

  resetPassword: (email, otp, newPassword) => fetch(`${API_BASE_URL}/auth/reset-password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, token: otp, newPassword })
  }).then(r => r.json().then(data => {
    if (!r.ok) throw new Error(data.error || 'Reset failed');
    return data;
  })),
};

export const userAPI = {
  getProfile: () => fetch(`${API_BASE_URL}/users/profile`, authHeaders())
    .then(r => r.json().then(data => {
      if (!r.ok) throw new Error(data.error || 'Failed to fetch profile');
      return data;
    })),
  
  updateProfile: (data) => fetch(`${API_BASE_URL}/users/profile`, {
    ...authHeaders(),
    method: 'PUT',
    body: JSON.stringify(data)
  }).then(r => r.json().then(data => {
    if (!r.ok) throw new Error(data.error || 'Failed to update profile');
    return data;
  })),

  enrollClass: (classId) => fetch(`${API_BASE_URL}/users/enroll/${classId}`, {
    ...authHeaders(),
    method: 'POST'
  }).then(r => r.json().then(data => {
    if (!r.ok) throw new Error(data.error || 'Failed to enroll in class');
    return data;
  })),

  getAllStudents: () => fetch(`${API_BASE_URL}/users/students`, authHeaders())
    .then(r => r.json().then(data => {
      if (!r.ok) throw new Error(data.error || 'Failed to fetch students');
      return data;
    })),

  getAllTeachers: async () => {
    // Fetch all users and filter for teachers
    const response = await fetch(`${API_BASE_URL}/admin/users`, authHeaders());
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Failed to fetch users');
    return Array.isArray(data) ? data.filter(u => u.role === 'teacher') : [];
  },
};

export const classAPI = {
  getMyClasses: () => fetch(`${API_BASE_URL}/classes/my`, authHeaders())
    .then(r => r.json().then(data => {
      if (!r.ok) throw new Error(data.error || 'Failed to fetch my classes');
      return data.classes || data;
    })),

  getAll: () => fetch(`${API_BASE_URL}/classes`, authHeaders())
    .then(r => r.json().then(data => {
      if (!r.ok) throw new Error(data.error || 'Failed to fetch classes');
      return data;
    })),

  getById: (id) => fetch(`${API_BASE_URL}/classes/${id}`, authHeaders())
    .then(r => r.json().then(data => {
      if (!r.ok) throw new Error(data.error || 'Class not found');
      return data;
    })),

  create: (data) => fetch(`${API_BASE_URL}/classes`, {
    ...authHeaders(),
    method: 'POST',
    body: JSON.stringify(data)
  }).then(r => r.json().then(data => {
    if (!r.ok) {
      const details = Array.isArray(data.details)
        ? data.details.map(({ msg }) => msg).filter(Boolean).join(', ')
        : '';
      throw new Error(details || data.error || 'Failed to create class');
    }
    return data;
  })),

  update: (id, data) => fetch(`${API_BASE_URL}/classes/${id}`, {
    ...authHeaders(),
    method: 'PUT',
    body: JSON.stringify(data)
  }).then(r => r.json().then(data => {
    if (!r.ok) throw new Error(data.error || 'Failed to update class');
    return data;
  })),

  delete: (id) => fetch(`${API_BASE_URL}/classes/${id}`, {
    ...authHeaders(),
    method: 'DELETE'
  }).then(r => r.json().then(data => {
    if (!r.ok) throw new Error(data.error || 'Failed to delete class');
    return data;
  })),

  enrollClass: (classId) => fetch(`${API_BASE_URL}/users/enroll/${classId}`, {
    ...authHeaders(),
    method: 'POST'
  }).then(r => r.json().then(data => {
    if (!r.ok) throw new Error(data.error || 'Failed to enroll in class');
    return data;
  }))
};

export const materialAPI = {
  upload: (classId, formData) => fetch(`${API_BASE_URL}/materials/upload/${classId}`, {
    headers: {
      'Authorization': `Bearer ${localStorage.getItem('token')}`
    },
    method: 'POST',
    body: formData
  }).then(r => r.json().then(data => {
    if (!r.ok) throw new Error(data.error || 'Failed to upload material');
    return data;
  })),

  getByClass: (classId) => fetch(`${API_BASE_URL}/materials/class/${classId}`, authHeaders())
    .then(r => r.json().then(data => {
      if (!r.ok) throw new Error(data.error || 'Failed to fetch materials');
      return data;
    })),

  delete: (id) => fetch(`${API_BASE_URL}/materials/${id}`, {
    ...authHeaders(),
    method: 'DELETE'
  }).then(r => r.json().then(data => {
    if (!r.ok) throw new Error(data.error || 'Failed to delete material');
    return data;
  }))
};

export const zoomAPI = {
  createMeeting: (classId) => fetch(`${API_BASE_URL}/zoom/create-meeting/${classId}`, {
    ...authHeaders(),
    method: 'POST'
  }).then(r => r.json().then(data => {
    if (!r.ok) throw new Error(data.error || 'Failed to create meeting');
    return data;
  })),

  getMeetingDetails: (meetingId) => fetch(`${API_BASE_URL}/zoom/meeting/${meetingId}`, authHeaders())
    .then(r => r.json().then(data => {
      if (!r.ok) throw new Error(data.error || 'Failed to fetch meeting details');
      return data;
    }))
};

export const adminAPI = {
  getStats: () => fetch(`${API_BASE_URL}/admin/stats`, authHeaders())
    .then(r => r.json().then(data => {
      if (!r.ok) throw new Error(data.error || 'Failed to fetch stats');
      return data;
    })),

  getAllUsers: () => fetch(`${API_BASE_URL}/admin/users`, authHeaders())
    .then(r => r.json().then(data => {
      if (!r.ok) throw new Error(data.error || 'Failed to fetch users');
      return data;
    })),

  getPendingUsers: () => fetch(`${API_BASE_URL}/admin/users/pending`, authHeaders())
    .then(r => r.json().then(data => {
      if (!r.ok) throw new Error(data.error || 'Failed to fetch pending users');
      return data;
    })),

  updateUserRole: (userId, role) => fetch(`${API_BASE_URL}/admin/users/${userId}/role`, {
    ...authHeaders(),
    method: 'PUT',
    body: JSON.stringify({ role })
  }).then(r => r.json().then(data => {
    if (!r.ok) throw new Error(data.error || 'Failed to update user role');
    return data;
  })),

  updateUserSubject: (userId, subject) => fetch(`${API_BASE_URL}/admin/users/${userId}/subject`, {
    ...authHeaders(),
    method: 'PUT',
    body: JSON.stringify({ subject })
  }).then(r => r.json().then(data => {
    if (!r.ok) throw new Error(data.error || 'Failed to update user subject');
    return data;
  })),

  deleteUser: (userId) => fetch(`${API_BASE_URL}/admin/users/${userId}`, {
    ...authHeaders(),
    method: 'DELETE'
  }).then(r => r.json().then(data => {
    if (!r.ok) throw new Error(data.error || 'Failed to delete user');
    return data;
  })),

  approveUser: (userId) => fetch(`${API_BASE_URL}/admin/users/${userId}/approve`, {
    ...authHeaders(),
    method: 'PUT'
  }).then(r => r.json().then(data => {
    if (!r.ok) throw new Error(data.error || 'Failed to approve user');
    return data;
  })),

  approveUsers: (ids) => fetch(`${API_BASE_URL}/admin/users/approve-batch`, {
    ...authHeaders(),
    method: 'PUT',
    body: JSON.stringify({ ids })
  }).then(r => r.json().then(data => {
    if (!r.ok) throw new Error(data.error || 'Failed to approve users');
    return data;
  })),

  getNotifications: () => fetch(`${API_BASE_URL}/admin/notifications`, authHeaders())
    .then(r => r.json().then(data => {
      if (!r.ok) throw new Error(data.error || 'Failed to fetch notifications');
      return data;
    })),

  markNotificationRead: (id) => fetch(`${API_BASE_URL}/admin/notifications/${id}/read`, {
    ...authHeaders(),
    method: 'PUT'
  }).then(r => r.json().then(data => {
    if (!r.ok) throw new Error(data.error || 'Failed to mark notification as read');
    return data;
  })),

  toggleUserActive: (userId, active) => fetch(`${API_BASE_URL}/admin/users/${userId}/active`, {
    ...authHeaders(),
    method: 'PUT',
    body: JSON.stringify({ active })
  }).then(r => r.json().then(data => {
    if (!r.ok) throw new Error(data.error || 'Failed to toggle user active status');
    return data;
  }))
};

export const feeAPI = {
  getByGrade: (grade) => fetch(`${API_BASE_URL}/fees/grade/${grade}`, authHeaders())
    .then(r => r.json().then(data => {
      if (!r.ok) throw new Error(data.error || 'Failed to fetch fee details');
      return data;
    })),

  getByStudent: (studentId) => fetch(`${API_BASE_URL}/fees/student/${studentId}`, authHeaders())
    .then(r => r.json().then(data => {
      if (!r.ok) throw new Error(data.error || 'Failed to fetch student fee details');
      return data;
    })),

  createOrUpdate: (feeData) => fetch(`${API_BASE_URL}/fees`, {
    ...authHeaders(),
    method: 'POST',
    body: JSON.stringify(feeData)
  }).then(r => r.json().then(data => {
    if (!r.ok) throw new Error(data.error || 'Failed to save fee details');
    return data;
  })),

  delete: (id) => fetch(`${API_BASE_URL}/fees/${id}`, {
    ...authHeaders(),
    method: 'DELETE'
  }).then(r => r.json().then(data => {
    if (!r.ok) throw new Error(data.error || 'Failed to delete fee record');
    return data;
  }))
};
export const attendanceAPI = {
  getClassAttendance: (classId) => fetch(`${API_BASE_URL}/attendance/class/${classId}`, authHeaders())
    .then(r => r.json().then(data => {
      if (!r.ok) throw new Error(data.error || 'Failed to fetch attendance');
      return data;
    })),

  getAttendanceSummary: (classId) => fetch(`${API_BASE_URL}/attendance/summary/${classId}`, authHeaders())
    .then(r => r.json().then(data => {
      if (!r.ok) throw new Error(data.error || 'Failed to fetch attendance summary');
      return data;
    })),

  getStudentAttendance: (classId, studentId) => fetch(`${API_BASE_URL}/attendance/student/${classId}/${studentId}`, authHeaders())
    .then(r => r.json().then(data => {
      if (!r.ok) throw new Error(data.error || 'Failed to fetch student attendance');
      return data;
    }))
};

export const notificationAPI = {
    // Get notifications sent to teachers
    getTeacherNotifications: () => fetch(`${API_BASE_URL}/notifications/teacher`, authHeaders())
      .then(async r => {
        const contentType = r.headers.get('content-type');
        let responseData;
        try {
          responseData = contentType && contentType.includes('application/json') 
            ? await r.json() 
            : await r.text();
        } catch (e) {
          throw new Error('Server error: Unable to parse response');
        }
        if (!r.ok) {
          const errorMsg = typeof responseData === 'string' ? 'Server error' : (responseData.error || 'Failed to fetch teacher notifications');
          throw new Error(errorMsg);
        }
        return responseData;
      }),

    // Delete teacher notification
    deleteTeacherNotification: (notificationId) => fetch(`${API_BASE_URL}/notifications/teacher/${notificationId}`, {
      ...authHeaders(),
      method: 'DELETE'
    }).then(async r => {
      const contentType = r.headers.get('content-type');
      let responseData;
      try {
        responseData = contentType && contentType.includes('application/json') 
          ? await r.json() 
          : await r.text();
      } catch (e) {
        throw new Error('Server error: Unable to parse response');
      }
      if (!r.ok) {
        const errorMsg = typeof responseData === 'string' ? 'Server error' : (responseData.error || 'Failed to delete teacher notification');
        throw new Error(errorMsg);
      }
      return responseData;
    }),
  create: (data) => fetch(`${API_BASE_URL}/notifications/create`, {
    ...authHeaders(),
    method: 'POST',
    body: JSON.stringify(data)
  }).then(async r => {
    const contentType = r.headers.get('content-type');
    let responseData;
    try {
      responseData = contentType && contentType.includes('application/json') 
        ? await r.json() 
        : await r.text();
    } catch (e) {
      throw new Error('Server error: Unable to parse response');
    }
    if (!r.ok) {
      const errorMsg = typeof responseData === 'string' ? 'Server error' : (responseData.error || 'Failed to create notification');
      throw new Error(errorMsg);
    }
    return responseData;
  }),

  createTeacherNotification: (data) => fetch(`${API_BASE_URL}/notifications/teacher/create`, {
    ...authHeaders(),
    method: 'POST',
    body: JSON.stringify(data)
  }).then(async r => {
    const contentType = r.headers.get('content-type');
    let responseData;
    try {
      responseData = contentType && contentType.includes('application/json') 
        ? await r.json() 
        : await r.text();
    } catch (e) {
      throw new Error('Server error: Unable to parse response');
    }
    if (!r.ok) {
      const errorMsg = typeof responseData === 'string' ? 'Server error' : (responseData.error || 'Failed to create teacher notification');
      throw new Error(errorMsg);
    }
    return responseData;
  }),

  getClassNotifications: (classId) => fetch(`${API_BASE_URL}/notifications/class/${classId}/latest`, authHeaders())
    .then(async r => {
      const contentType = r.headers.get('content-type');
      let responseData;
      try {
        responseData = contentType && contentType.includes('application/json') 
          ? await r.json() 
          : await r.text();
      } catch (e) {
        throw new Error('Server error: Unable to parse response');
      }
      if (!r.ok) {
        const errorMsg = typeof responseData === 'string' ? 'Server error' : (responseData.error || 'Failed to fetch notifications');
        throw new Error(errorMsg);
      }
      return responseData;
    }),

  getAllClassNotifications: (classId) => fetch(`${API_BASE_URL}/notifications/class/${classId}`, authHeaders())
    .then(async r => {
      const contentType = r.headers.get('content-type');
      let responseData;
      try {
        responseData = contentType && contentType.includes('application/json') 
          ? await r.json() 
          : await r.text();
      } catch (e) {
        throw new Error('Server error: Unable to parse response');
      }
      if (!r.ok) {
        const errorMsg = typeof responseData === 'string' ? 'Server error' : (responseData.error || 'Failed to fetch notifications');
        throw new Error(errorMsg);
      }
      return responseData;
    }),

  markAsRead: (notificationId) => fetch(`${API_BASE_URL}/notifications/${notificationId}/read`, {
    ...authHeaders(),
    method: 'PUT'
  }).then(async r => {
    const contentType = r.headers.get('content-type');
    let responseData;
    try {
      responseData = contentType && contentType.includes('application/json') 
        ? await r.json() 
        : await r.text();
    } catch (e) {
      throw new Error('Server error: Unable to parse response');
    }
    if (!r.ok) {
      const errorMsg = typeof responseData === 'string' ? 'Server error' : (responseData.error || 'Failed to mark as read');
      throw new Error(errorMsg);
    }
    return responseData;
  }),

  delete: (notificationId) => fetch(`${API_BASE_URL}/notifications/${notificationId}`, {
    ...authHeaders(),
    method: 'DELETE'
  }).then(async r => {
    const contentType = r.headers.get('content-type');
    let responseData;
    try {
      responseData = contentType && contentType.includes('application/json') 
        ? await r.json() 
        : await r.text();
    } catch (e) {
      throw new Error('Server error: Unable to parse response');
    }
    if (!r.ok) {
      const errorMsg = typeof responseData === 'string' ? 'Server error' : (responseData.error || 'Failed to delete notification');
      throw new Error(errorMsg);
    }
    return responseData;
  }),

  getDashboardNotifications: () => fetch(`${API_BASE_URL}/notifications/dashboard/all`, authHeaders())
    .then(async r => {
      const contentType = r.headers.get('content-type');
      let responseData;
      try {
        responseData = contentType && contentType.includes('application/json') 
          ? await r.json() 
          : await r.text();
      } catch (e) {
        throw new Error('Server error: Unable to parse response');
      }
      if (!r.ok) {
        const errorMsg = typeof responseData === 'string' ? 'Server error' : (responseData.error || 'Failed to fetch dashboard notifications');
        throw new Error(errorMsg);
      }
      return responseData;
    })
};

export const topperAPI = {
  getByClass: (classId) => fetch(`${API_BASE_URL}/toppers/class/${classId}`, authHeaders())
    .then(r => r.json().then(data => {
      if (!r.ok) throw new Error(data.error || 'Failed to fetch toppers');
      return data;
    })),

  getAll: () => fetch(`${API_BASE_URL}/toppers/all`, authHeaders())
    .then(r => r.json().then(data => {
      if (!r.ok) throw new Error(data.error || 'Failed to fetch toppers');
      return data;
    })),

  createOrUpdate: (data) => fetch(`${API_BASE_URL}/toppers`, {
    ...authHeaders(),
    method: 'POST',
    body: JSON.stringify(data)
  }).then(r => r.json().then(data => {
    if (!r.ok) throw new Error(data.error || 'Failed to save toppers');
    return data;
  }))
};

export const resultAPI = {
  create: (data) => fetch(`${API_BASE_URL}/results`, {
    ...authHeaders(),
    method: 'POST',
    body: JSON.stringify(data)
  }).then(r => r.json().then(data => {
    if (!r.ok) throw new Error(data.error || data.message || 'Failed to create result');
    return data;
  })),

  getByClass: (classId) => fetch(`${API_BASE_URL}/results/class/${classId}`, authHeaders())
    .then(r => r.json().then(data => {
      if (!r.ok) throw new Error(data.error || data.message || 'Failed to fetch results');
      return data;
    })),

  getByStudent: (studentId) => fetch(`${API_BASE_URL}/results/student/${studentId}`, authHeaders())
    .then(r => r.json().then(data => {
      if (!r.ok) throw new Error(data.error || data.message || 'Failed to fetch results');
      return data;
    })),

  getMyResults: () => fetch(`${API_BASE_URL}/results/my-results`, authHeaders())
    .then(r => r.json().then(data => {
      if (!r.ok) throw new Error(data.error || data.message || 'Failed to fetch results');
      return data;
    })),

  getAll: () => fetch(`${API_BASE_URL}/results/all`, authHeaders())
    .then(r => r.json().then(data => {
      if (!r.ok) throw new Error(data.error || data.message || 'Failed to fetch results');
      return data;
    })),

  delete: (resultId) => fetch(`${API_BASE_URL}/results/${resultId}`, {
    ...authHeaders(),
    method: 'DELETE'
  }).then(r => r.json().then(data => {
    if (!r.ok) throw new Error(data.error || data.message || 'Failed to delete result');
    return data;
  }))
};

export const productAPI = {
  getAll: () => fetch(`${API_BASE_URL}/products`)
    .then(r => r.json().then(data => {
      if (!r.ok) throw new Error(data.error || 'Failed to fetch products');
      return data;
    })),

  create: (data) => fetch(`${API_BASE_URL}/products`, {
    ...authHeaders(),
    method: 'POST',
    body: JSON.stringify(data)
  }).then(r => r.json().then(data => {
    if (!r.ok) throw new Error(data.error || 'Failed to create product');
    return data;
  })),

  update: (id, data) => fetch(`${API_BASE_URL}/products/${id}`, {
    ...authHeaders(),
    method: 'PUT',
    body: JSON.stringify(data)
  }).then(r => r.json().then(data => {
    if (!r.ok) throw new Error(data.error || 'Failed to update product');
    return data;
  })),

  delete: (id) => fetch(`${API_BASE_URL}/products/${id}`, {
    ...authHeaders(),
    method: 'DELETE'
  }).then(r => r.json().then(data => {
    if (!r.ok) throw new Error(data.error || 'Failed to delete product');
    return data;
  })),

  uploadImage: (filename, contentType, imageBase64) => fetch(`${API_BASE_URL}/product-images/upload`, {
    ...authHeaders(),
    method: 'POST',
    body: JSON.stringify({ filename, contentType, imageBase64 })
  }).then(r => r.json().then(data => {
    if (!r.ok) throw new Error(data.error || 'Failed to upload image');
    return data; // { id, url }
  })),
};

// AKSHAY Incharge product API — PIN-verified on server via x-incharge-pin header
export const inchargeProductAPI = {
  create: (data) => fetch(`${API_BASE_URL}/akshay/products`, {
    method: 'POST',
    headers: pinHeaders('akshay'),
    body: JSON.stringify(data)
  }).then(r => r.json().then(d => {
    if (!r.ok) throw new Error(d.error || 'Failed to create product');
    return d;
  })),

  update: (id, data) => fetch(`${API_BASE_URL}/akshay/products/${id}`, {
    method: 'PUT',
    headers: pinHeaders('akshay'),
    body: JSON.stringify(data)
  }).then(r => r.json().then(d => {
    if (!r.ok) throw new Error(d.error || 'Failed to update product');
    return d;
  })),

  delete: (id) => fetch(`${API_BASE_URL}/akshay/products/${id}`, {
    method: 'DELETE',
    headers: pinHeaders('akshay')
  }).then(r => r.json().then(d => {
    if (!r.ok) throw new Error(d.error || 'Failed to delete product');
    return d;
  })),

  uploadImage: (filename, contentType, imageBase64) => fetch(`${API_BASE_URL}/akshay/product-images/upload`, {
    method: 'POST',
    headers: pinHeaders('akshay'),
    body: JSON.stringify({ filename, contentType, imageBase64 })
  }).then(r => r.json().then(d => {
    if (!r.ok) throw new Error(d.error || 'Failed to upload image');
    return d; // { id, url }
  })),
};

export const paymentSettingsAPI = {
  get: () => fetch(`${API_BASE_URL}/akshay/payment-settings`)
    .then(r => r.json().then(d => {
      if (!r.ok) throw new Error(d.error || 'Failed to fetch payment settings');
      return d;
    })),

  update: (conditions) => fetch(`${API_BASE_URL}/akshay/payment-settings`, {
    method: 'PUT',
    headers: pinHeaders('akshay'),
    body: JSON.stringify({ conditions })
  }).then(r => r.json().then(d => {
    if (!r.ok) throw new Error(d.error || 'Failed to update payment settings');
    return d;
  })),

  uploadQR: (filename, contentType, imageBase64) => fetch(`${API_BASE_URL}/akshay/payment-settings/qr-upload`, {
    method: 'POST',
    headers: pinHeaders('akshay'),
    body: JSON.stringify({ filename, contentType, imageBase64 })
  }).then(r => r.json().then(d => {
    if (!r.ok) throw new Error(d.error || 'Failed to upload QR code');
    return d; // { qrCodeUrl, settings }
  })),
};

export const orderAPI = {
  place: (data) => fetch(`${API_BASE_URL}/orders`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data)
  }).then(r => r.json().then(data => {
    if (!r.ok) throw new Error(data.error || 'Failed to place order');
    return data;
  })),

  getAll: () => fetch(`${API_BASE_URL}/orders`, authHeaders())
    .then(r => r.json().then(data => {
      if (!r.ok) throw new Error(data.error || 'Failed to fetch orders');
      return data;
    })),

  // For Incharge panel — PIN required via header
  getAllIncharge: () => fetch(`${API_BASE_URL}/orders/incharge`, {
    headers: pinHeaders('akshay')
  })
    .then(r => r.json().then(data => {
      if (!r.ok) throw new Error(data.error || 'Failed to fetch orders');
      return data;
    })),

  toggleDeliver: (id) => fetch(`${API_BASE_URL}/orders/${id}/deliver`, {
    ...authHeaders(),
    method: 'PATCH'
  }).then(r => r.json().then(data => {
    if (!r.ok) throw new Error(data.error || 'Failed to update delivery status');
    return data;
  })),

  // For Incharge panel — PIN required via header
  toggleDeliverIncharge: (id) => fetch(`${API_BASE_URL}/orders/${id}/deliver/incharge`, {
    method: 'PATCH',
    headers: pinHeaders('akshay')
  }).then(r => r.json().then(data => {
    if (!r.ok) throw new Error(data.error || 'Failed to update delivery status');
    return data;
  })),

  delete: (id) => fetch(`${API_BASE_URL}/orders/${id}`, {
    ...authHeaders(),
    method: 'DELETE'
  }).then(r => r.json().then(data => {
    if (!r.ok) throw new Error(data.error || 'Failed to delete order');
    return data;
  })),

  // Delete all orders on a specific local calendar date (admin only)
  deleteByDate: (dateKey) => {
    const [y, m, d] = dateKey.split('-').map(Number);
    const start = new Date(y, m - 1, d, 0, 0, 0, 0).toISOString();
    const end   = new Date(y, m - 1, d, 23, 59, 59, 999).toISOString();
    return fetch(
      `${API_BASE_URL}/orders/by-date?start=${encodeURIComponent(start)}&end=${encodeURIComponent(end)}`,
      { ...authHeaders(), method: 'DELETE' }
    ).then(r => r.json().then(data => {
      if (!r.ok) throw new Error(data.error || 'Failed to delete orders by date');
      return data;
    }));
  },

  // Delete all orders in a specific month "YYYY-MM" (admin only)
  deleteByMonth: (month) => {
    const [y, mo] = month.split('-').map(Number);
    const start = new Date(y, mo - 1, 1, 0, 0, 0, 0).toISOString();
    const end   = new Date(y, mo,     1, 0, 0, 0, 0).toISOString(); // first of next month (exclusive)
    return fetch(
      `${API_BASE_URL}/orders/by-month?start=${encodeURIComponent(start)}&end=${encodeURIComponent(end)}`,
      { ...authHeaders(), method: 'DELETE' }
    ).then(r => r.json().then(data => {
      if (!r.ok) throw new Error(data.error || 'Failed to delete orders by month');
      return data;
    }));
  },

  // Download monthly sales report PDF (admin only)
  downloadMonthlyReport: async (month) => {
    const r = await fetch(
      `${API_BASE_URL}/orders/report/monthly?month=${encodeURIComponent(month)}`,
      authHeaders()
    );
    if (!r.ok) {
      const data = await r.json().catch(() => ({}));
      throw new Error(data.error || 'Failed to generate report');
    }
    return r.blob();
  },
};

export const galleryAPI = {
  getAll: () => fetch(`${API_BASE_URL}/gallery`)
    .then(r => r.json().then(data => {
      if (!r.ok) throw new Error(data.error || 'Failed to fetch gallery');
      return data;
    })),

  addYoutube: (url, title) => fetch(`${API_BASE_URL}/gallery`, {
    ...authHeaders(),
    method: 'POST',
    body: JSON.stringify({ type: 'youtube', url, title })
  }).then(r => r.json().then(data => {
    if (!r.ok) throw new Error(data.error || 'Failed to add YouTube item');
    return data;
  })),

  addImage: (imageBase64, contentType, title) => fetch(`${API_BASE_URL}/gallery`, {
    ...authHeaders(),
    method: 'POST',
    body: JSON.stringify({ type: 'image', imageBase64, contentType, title })
  }).then(r => r.json().then(data => {
    if (!r.ok) throw new Error(data.error || 'Failed to upload image');
    return data;
  })),

  delete: (id) => fetch(`${API_BASE_URL}/gallery/${id}`, {
    ...authHeaders(),
    method: 'DELETE'
  }).then(r => r.json().then(data => {
    if (!r.ok) throw new Error(data.error || 'Failed to delete gallery item');
    return data;
  })),
};

export const noticeAPI = {
  getAll: () => fetch(`${API_BASE_URL}/notices`)
    .then(r => r.json().then(data => {
      if (!r.ok) throw new Error(data.error || 'Failed to fetch notices');
      return data;
    })),

  create: (text, color) => fetch(`${API_BASE_URL}/notices`, {
    ...authHeaders(),
    method: 'POST',
    body: JSON.stringify({ text, color })
  }).then(r => r.json().then(data => {
    if (!r.ok) throw new Error(data.error || 'Failed to create notice');
    return data;
  })),

  delete: (id) => fetch(`${API_BASE_URL}/notices/${id}`, {
    ...authHeaders(),
    method: 'DELETE'
  }).then(r => r.json().then(data => {
    if (!r.ok) throw new Error(data.error || 'Failed to delete notice');
    return data;
  })),
};

/* ─── Canteen Menu API ───────────────────────────────────────────────────── */
export const canteenMenuAPI = {
  getAll: () => fetch(`${API_BASE_URL}/canteen/menu`)
    .then(r => r.json().then(data => { if (!r.ok) throw new Error(data.error || 'Failed to fetch menu'); return data; })),

  create: (data) => fetch(`${API_BASE_URL}/canteen/menu`, {
    method: 'POST', headers: pinHeaders('canteen'), body: JSON.stringify(data)
  }).then(r => r.json().then(d => { if (!r.ok) throw new Error(d.error || 'Failed to add item'); return d; })),

  update: (id, data) => fetch(`${API_BASE_URL}/canteen/menu/${id}`, {
    method: 'PUT', headers: pinHeaders('canteen'), body: JSON.stringify(data)
  }).then(r => r.json().then(d => { if (!r.ok) throw new Error(d.error || 'Failed to update item'); return d; })),

  delete: (id) => fetch(`${API_BASE_URL}/canteen/menu/${id}`, { method: 'DELETE', headers: pinHeaders('canteen') })
    .then(r => r.json().then(d => { if (!r.ok) throw new Error(d.error || 'Failed to delete item'); return d; })),
};

/* ─── Canteen Order API ──────────────────────────────────────────────────── */
export const canteenOrderAPI = {
  place: (data) => fetch(`${API_BASE_URL}/canteen/orders`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data)
  }).then(r => r.json().then(d => { if (!r.ok) throw new Error(d.error || 'Failed to place order'); return d; })),

  getAll: () => fetch(`${API_BASE_URL}/canteen/orders`, authHeaders())
    .then(r => r.json().then(d => { if (!r.ok) throw new Error(d.error || 'Failed to fetch orders'); return d; })),

  getAllIncharge: () => fetch(`${API_BASE_URL}/canteen/orders/incharge`, {
    headers: pinHeaders('canteen')
  })
    .then(r => r.json().then(d => { if (!r.ok) throw new Error(d.error || 'Failed to fetch orders'); return d; })),

  toggleDeliver: (id) => fetch(`${API_BASE_URL}/canteen/orders/${id}/deliver`, { ...authHeaders(), method: 'PATCH' })
    .then(r => r.json().then(d => { if (!r.ok) throw new Error(d.error || 'Failed to update'); return d; })),

  toggleDeliverIncharge: (id) => fetch(`${API_BASE_URL}/canteen/orders/${id}/deliver/incharge`, {
    method: 'PATCH', headers: pinHeaders('canteen')
  }).then(r => r.json().then(d => { if (!r.ok) throw new Error(d.error || 'Failed to update'); return d; })),

  delete: (id) => fetch(`${API_BASE_URL}/canteen/orders/${id}`, { ...authHeaders(), method: 'DELETE' })
    .then(r => r.json().then(d => { if (!r.ok) throw new Error(d.error || 'Failed to delete'); return d; })),

  deleteByDate: (dateKey) => {
    const [y, m, d] = dateKey.split('-').map(Number);
    const start = new Date(y, m - 1, d, 0, 0, 0, 0).toISOString();
    const end   = new Date(y, m - 1, d, 23, 59, 59, 999).toISOString();
    return fetch(`${API_BASE_URL}/canteen/orders/by-date?start=${encodeURIComponent(start)}&end=${encodeURIComponent(end)}`,
      { ...authHeaders(), method: 'DELETE' }
    ).then(r => r.json().then(d => { if (!r.ok) throw new Error(d.error || 'Failed'); return d; }));
  },

  deleteByMonth: (month) => {
    const [y, mo] = month.split('-').map(Number);
    const start = new Date(y, mo - 1, 1, 0, 0, 0, 0).toISOString();
    const end   = new Date(y, mo,     1, 0, 0, 0, 0).toISOString();
    return fetch(`${API_BASE_URL}/canteen/orders/by-month?start=${encodeURIComponent(start)}&end=${encodeURIComponent(end)}`,
      { ...authHeaders(), method: 'DELETE' }
    ).then(r => r.json().then(d => { if (!r.ok) throw new Error(d.error || 'Failed'); return d; }));
  },

  downloadMonthlyReport: async (month) => {
    const r = await fetch(`${API_BASE_URL}/canteen/orders/report/monthly?month=${encodeURIComponent(month)}`, authHeaders());
    if (!r.ok) { const d = await r.json().catch(() => ({})); throw new Error(d.error || 'Failed to generate report'); }
    return r.blob();
  },
};

/* ─── Admin Incharge PIN Management ──────────────────────────────────────── */
export const inchargePinAPI = {
  setPin: (role, pin) => fetch(`${API_BASE_URL}/admin/incharge-pin/${role}`, {
    ...authHeaders(),
    method: 'PUT',
    body: JSON.stringify({ pin })
  }).then(r => r.json().then(d => {
    if (!r.ok) throw new Error(d.error || 'Failed to set PIN');
    return d;
  })),

  verify: (role, pin) => fetch(`${API_BASE_URL}/admin/incharge-pin/verify`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ role, pin })
  }).then(r => r.json().then(d => {
    if (!r.ok) throw new Error(d.error || 'Incorrect PIN');
    return d;
  })),
};

/* ─── 2FA API ────────────────────────────────────────────────────────────── */
export const twoFactorAPI = {
  setup: () => fetch(`${API_BASE_URL}/auth/2fa/setup`, {
    ...authHeaders(),
    method: 'POST'
  }).then(r => r.json().then(d => {
    if (!r.ok) throw new Error(d.error || 'Failed to set up 2FA');
    return d; // { qrCode, manualKey, message }
  })),

  verifySetup: (code) => fetch(`${API_BASE_URL}/auth/2fa/verify-setup`, {
    ...authHeaders(),
    method: 'POST',
    body: JSON.stringify({ code })
  }).then(r => r.json().then(d => {
    if (!r.ok) throw new Error(d.error || 'Failed to verify 2FA code');
    return d;
  })),

  disable: (code) => fetch(`${API_BASE_URL}/auth/2fa/disable`, {
    ...authHeaders(),
    method: 'POST',
    body: JSON.stringify({ code })
  }).then(r => r.json().then(d => {
    if (!r.ok) throw new Error(d.error || 'Failed to disable 2FA');
    return d;
  })),
};

/* ─── Test Generator API ─────────────────────────────────────────────────── */
export const testGeneratorAPI = {
  // Question Bank
  uploadQuestionBank: (formData) => fetch(`${API_BASE_URL}/test-generator/question-bank/upload`, {
    method: 'POST',
    headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
    body: formData,
  }).then(r => r.json().then(d => {
    if (!r.ok) throw new Error(d.error || 'Upload failed');
    return d;
  })),

  listQuestionBanks: (query = '') => fetch(`${API_BASE_URL}/test-generator/question-bank${query}`, authHeaders())
    .then(r => r.json().then(d => {
      if (!r.ok) throw new Error(d.error || 'Failed to load question banks');
      return d;
    })),

  deleteQuestionBank: (id) => fetch(`${API_BASE_URL}/test-generator/question-bank/${id}`, {
    ...authHeaders(),
    method: 'DELETE',
  }).then(r => r.json().then(d => {
    if (!r.ok) throw new Error(d.error || 'Delete failed');
    return d;
  })),

  // Test Generation — accepts FormData (multipart); do NOT set Content-Type (browser sets boundary)
  generateTest: (formData) => {
    const headers = authHeaders();
    // Remove Content-Type so fetch sets the correct multipart/form-data boundary
    delete headers.headers['Content-Type'];
    return fetch(`${API_BASE_URL}/test-generator/generate`, {
      ...headers,
      method: 'POST',
      body: formData,
    }).then(r => r.json().then(d => {
      if (!r.ok) throw new Error(d.error || 'Generation failed');
      return d;
    }));
  },

  listTestPapers: () => fetch(`${API_BASE_URL}/test-generator/papers`, authHeaders())
    .then(r => r.json().then(d => {
      if (!r.ok) throw new Error(d.error || 'Failed to load papers');
      return d;
    })),

  deleteTestPaper: (id) => fetch(`${API_BASE_URL}/test-generator/papers/${id}`, {
    ...authHeaders(),
    method: 'DELETE',
  }).then(r => r.json().then(d => {
    if (!r.ok) throw new Error(d.error || 'Delete failed');
    return d;
  })),

  regenerateTest: (id) => fetch(`${API_BASE_URL}/test-generator/papers/${id}/regenerate`, {
    ...authHeaders(),
    method: 'POST',
  }).then(r => r.json().then(d => {
    if (!r.ok) throw new Error(d.error || 'Regeneration failed');
    return d;
  })),
};

/* ─── Banner API ─────────────────────────────────────────────────────────── */
export const bannerAPI = {
  getAll: () => fetch(`${API_BASE_URL}/banners`)
    .then(r => r.json().then(data => {
      if (!r.ok) throw new Error(data.error || 'Failed to fetch banners');
      return data;
    })),

  upload: (imageBase64, contentType, title) => fetch(`${API_BASE_URL}/banners`, {
    ...authHeaders(),
    method: 'POST',
    body: JSON.stringify({ imageBase64, contentType, title })
  }).then(r => r.json().then(data => {
    if (!r.ok) throw new Error(data.error || 'Failed to upload banner');
    return data;
  })),

  delete: (id) => fetch(`${API_BASE_URL}/banners/${id}`, {
    ...authHeaders(),
    method: 'DELETE'
  }).then(r => r.json().then(data => {
    if (!r.ok) throw new Error(data.error || 'Failed to delete banner');
    return data;
  })),
};